import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/config/firebase';
import {
  listWorkspacePayouts,
  markPayoutAsPaidOut,
  WorkspacePayoutItem,
  WorkspacePayoutPolicy,
  WorkspacePayoutSummary,
} from '@/lib/lendliftedService';
import { Timestamp, collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { toast } from 'sonner';

const formatMoney = (cents: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(Math.max(0, cents) / 100);

const EMPTY_SUMMARY: WorkspacePayoutSummary = {
  pendingBalanceCents: 0,
  pendingCount: 0,
  paidOutBalanceCents: 0,
  paidOutCount: 0,
  refundedBalanceCents: 0,
  disputedCount: 0,
  hasConnectAccount: false,
};

const EMPTY_POLICY: WorkspacePayoutPolicy = {
  trustTier: 'new',
  riskStatus: 'normal',
  payoutSchedule: 'weekly',
  payoutWeekday: 2,
  payoutHoldDays: 7,
  allowManualPayouts: true,
  manualPayoutCapCents: 50000,
  reserveBps: 1000,
};

const toIsoOrNull = (value: unknown): string | null => {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString();
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate().toISOString();
  }
  return typeof value === 'string' ? value : null;
};

const buildSummaryFromItems = (items: WorkspacePayoutItem[], hasConnectAccount: boolean): WorkspacePayoutSummary => {
  const pendingStatuses = new Set(['pending_connect', 'pending_payout_ready']);
  const pending = items.filter((p) => pendingStatuses.has(p.status));
  const paidOut = items.filter((p) => p.status === 'paid_out');
  const refunded = items.filter((p) => p.status === 'refunded');
  const disputed = items.filter((p) => p.status === 'dispute_open');
  const sum = (arr: WorkspacePayoutItem[]) => arr.reduce((acc, item) => acc + Math.max(0, item.sellerNetCents || 0), 0);
  return {
    pendingBalanceCents: sum(pending),
    pendingCount: pending.length,
    paidOutBalanceCents: sum(paidOut),
    paidOutCount: paidOut.length,
    refundedBalanceCents: sum(refunded),
    disputedCount: disputed.length,
    hasConnectAccount,
  };
};

const PayoutsSettings = () => {
  const { userSettings } = useAuth();
  const workspaceId = userSettings?.activeWorkspaceId;
  const [isLoading, setIsLoading] = useState(true);
  const [isMarkingQuoteId, setIsMarkingQuoteId] = useState<string | null>(null);
  const [items, setItems] = useState<WorkspacePayoutItem[]>([]);
  const [summary, setSummary] = useState<WorkspacePayoutSummary>(EMPTY_SUMMARY);
  const [policy, setPolicy] = useState<WorkspacePayoutPolicy>(EMPTY_POLICY);

  const activeWorkspaceRole = userSettings?.workspaces?.find((ws) => ws.id === workspaceId)?.role;
  const canManagePayouts = activeWorkspaceRole === 'owner' || activeWorkspaceRole === 'admin';

  const loadPolicy = async () => {
    if (!workspaceId) {
      setItems([]);
      setSummary(EMPTY_SUMMARY);
      setPolicy(EMPTY_POLICY);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const data = await listWorkspacePayouts(workspaceId, 200);
      setPolicy(data.policy || EMPTY_POLICY);
      setSummary((current) => ({ ...current, hasConnectAccount: data.summary.hasConnectAccount }));
    } catch (error: any) {
      console.error('Failed to load workspace payouts', error);
      toast.error('Could not load payouts right now.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadPolicy();
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) {
      setItems([]);
      setSummary(EMPTY_SUMMARY);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const ledgerQuery = query(
      collection(db, 'workspaces', workspaceId, 'payoutLedger'),
      orderBy('createdAt', 'desc'),
      limit(200)
    );

    const unsubscribe = onSnapshot(
      ledgerQuery,
      (snapshot) => {
        const nextItems = snapshot.docs.map((doc) => {
          const data = doc.data() as Record<string, unknown>;
          return {
            quoteId: String(data.quoteId || doc.id),
            sellerNetCents: Number(data.sellerNetCents || 0),
            amountTotalCents: Number(data.amountTotalCents || 0),
            platformFeeAmountCents: Number(data.platformFeeAmountCents || 0),
            currency: String(data.currency || 'USD'),
            status: String(data.status || 'pending_connect'),
            createdAt: toIsoOrNull(data.createdAt),
            paidOutAt: toIsoOrNull(data.paidOutAt),
            refundedAt: toIsoOrNull(data.refundedAt),
            disputeOpenedAt: toIsoOrNull(data.disputeOpenedAt),
            disputeClosedAt: toIsoOrNull(data.disputeClosedAt),
            payoutNote: data.payoutNote ? String(data.payoutNote) : null,
            stripeCheckoutSessionId: data.stripeCheckoutSessionId ? String(data.stripeCheckoutSessionId) : null,
            stripePaymentIntentId: data.stripePaymentIntentId ? String(data.stripePaymentIntentId) : null,
          } satisfies WorkspacePayoutItem;
        });

        setItems(nextItems);
        setSummary((current) => buildSummaryFromItems(nextItems, current.hasConnectAccount));
        setIsLoading(false);
      },
      (error) => {
        console.error('Failed to subscribe to payout ledger', error);
        toast.error('Could not subscribe to payout updates.');
        setIsLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [workspaceId]);

  const pendingItems = useMemo(
    () => items.filter((item) => item.status === 'pending_connect' || item.status === 'pending_payout_ready'),
    [items]
  );
  const paidOutItems = useMemo(() => items.filter((item) => item.status === 'paid_out'), [items]);
  const riskItems = useMemo(
    () => items.filter((item) => ['refunded', 'dispute_open', 'dispute_won', 'dispute_lost'].includes(item.status)),
    [items]
  );
  const weekdayLabel = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][policy.payoutWeekday] || 'Tuesday';

  const handleMarkPaidOut = async (quoteId: string) => {
    if (!workspaceId) return;
    setIsMarkingQuoteId(quoteId);
    try {
      await markPayoutAsPaidOut(workspaceId, quoteId);
      toast.success('Payout marked as paid out.');
      await loadPolicy();
    } catch (error: any) {
      console.error('Failed to mark payout as paid out', error);
      toast.error(error?.message || 'Could not mark payout as paid out.');
    } finally {
      setIsMarkingQuoteId(null);
    }
  };

  return (
    <div className="space-y-8">
      <Card className="rounded-3xl border-slate-200 bg-white">
        <CardHeader className="p-8 pb-4">
          <CardTitle className="text-2xl tracking-[-0.03em] text-slate-950">Payouts</CardTitle>
          <CardDescription className="text-slate-600">Track held payments, payout history, and refund/dispute events.</CardDescription>
        </CardHeader>
        <CardContent className="p-8 pt-2 space-y-6">
          <div className="grid gap-3 md:grid-cols-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
              <p className="text-xs uppercase tracking-[0.12em] text-slate-500">Held Balance</p>
              <p className="mt-1 text-xl font-semibold tracking-[-0.02em] text-slate-900">{formatMoney(summary.pendingBalanceCents)}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
              <p className="text-xs uppercase tracking-[0.12em] text-slate-500">Held Payments</p>
              <p className="mt-1 text-xl font-semibold tracking-[-0.02em] text-slate-900">{summary.pendingCount}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
              <p className="text-xs uppercase tracking-[0.12em] text-slate-500">Paid Out</p>
              <p className="mt-1 text-xl font-semibold tracking-[-0.02em] text-slate-900">{formatMoney(summary.paidOutBalanceCents)}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
              <p className="text-xs uppercase tracking-[0.12em] text-slate-500">Risk Events</p>
              <p className="mt-1 text-xl font-semibold tracking-[-0.02em] text-slate-900">
                {summary.disputedCount + riskItems.filter((item) => item.status === 'refunded').length}
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-cyan-200 bg-cyan-50/80 p-4 text-sm text-slate-700">
            <p className="font-semibold">Payout policy</p>
            <p className="mt-1">
              {policy.payoutSchedule === 'daily'
                ? 'Payout-ready funds are reviewed daily.'
                : `Payout-ready funds are reviewed weekly on ${weekdayLabel}.`}{' '}
              New payments are held for {policy.payoutHoldDays} day{policy.payoutHoldDays === 1 ? '' : 's'} before release.
            </p>
            <p className="mt-1">
              Fast manual release cap: {formatMoney(policy.manualPayoutCapCents)}. Account health: {policy.riskStatus.replace('_', ' ')}.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button asChild className="rounded-xl bg-slate-900 text-white hover:bg-slate-800">
              <Link to="/onboarding">Finish payout setup</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-xl border-slate-200 bg-white hover:bg-slate-50">
              <Link to="/studio">Back to Studio</Link>
            </Button>
          </div>

          {!summary.hasConnectAccount && (
            <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
              Complete payout onboarding first. You can still sell now, and held payments will stay tracked here.
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-6 text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading payout ledger...
            </div>
          ) : (
            <>
              <div className="space-y-3">
                <h3 className="text-base font-semibold tracking-[-0.02em] text-slate-900">Held payments</h3>
                {pendingItems.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">
                    No held payments at the moment.
                  </div>
                ) : (
                  pendingItems.map((item) => (
                    <div key={item.quoteId} className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="font-semibold text-slate-900">
                            {formatMoney(item.sellerNetCents, item.currency)} pending cashout
                          </p>
                          <p className="text-xs text-slate-500">
                            Quote #{item.quoteId.slice(0, 8)} • Gross {formatMoney(item.amountTotalCents, item.currency)} • Fees{' '}
                            {formatMoney(item.platformFeeAmountCents, item.currency)}
                          </p>
                        </div>
                        <span className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800">
                          {item.status.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <p className="text-xs text-slate-500">
                          Captured: {item.createdAt ? new Date(item.createdAt).toLocaleString() : 'unknown'}
                        </p>
                        {canManagePayouts && (
                          <Button
                            type="button"
                            size="sm"
                            className="h-8 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
                            disabled={
                              !summary.hasConnectAccount ||
                              isMarkingQuoteId === item.quoteId ||
                              (item.status === 'pending_connect' && !policy.allowManualPayouts) ||
                              policy.riskStatus === 'restricted' ||
                              policy.riskStatus === 'frozen'
                            }
                            onClick={() => handleMarkPaidOut(item.quoteId)}
                          >
                            {isMarkingQuoteId === item.quoteId ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
                            Mark as paid out
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="space-y-3">
                <h3 className="text-base font-semibold tracking-[-0.02em] text-slate-900">Completed payouts</h3>
                {paidOutItems.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">
                    No payouts recorded yet.
                  </div>
                ) : (
                  paidOutItems.map((item) => (
                    <div key={item.quoteId} className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="font-semibold text-slate-900">{formatMoney(item.sellerNetCents, item.currency)} paid out</p>
                          <p className="text-xs text-slate-500">Quote #{item.quoteId.slice(0, 8)}</p>
                        </div>
                        <span className="rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
                          paid out
                        </span>
                      </div>
                      <p className="mt-2 text-xs text-slate-500">
                        Paid: {item.paidOutAt ? new Date(item.paidOutAt).toLocaleString() : 'unknown'}
                      </p>
                    </div>
                  ))
                )}
              </div>

              <div className="space-y-3">
                <h3 className="text-base font-semibold tracking-[-0.02em] text-slate-900">Refunds and disputes</h3>
                {riskItems.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">
                    No refund or dispute events.
                  </div>
                ) : (
                  riskItems.map((item) => (
                    <div key={item.quoteId} className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="font-semibold text-slate-900">
                        Quote #{item.quoteId.slice(0, 8)} • {item.status.replace(/_/g, ' ')}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        Amount: {formatMoney(item.sellerNetCents, item.currency)}
                        {item.refundedAt ? ` • Refunded ${new Date(item.refundedAt).toLocaleString()}` : ''}
                        {item.disputeOpenedAt ? ` • Dispute opened ${new Date(item.disputeOpenedAt).toLocaleString()}` : ''}
                        {item.disputeClosedAt ? ` • Dispute closed ${new Date(item.disputeClosedAt).toLocaleString()}` : ''}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PayoutsSettings;
